import type { ArcaEnvironment, IvaRateOption, TaxRegime } from './fiscalTypes';
import { formatMoneyCompact } from '../../shared/format/public-api';

/** Límite anual por categoría monotributo — ingresos brutos (AFIP, locaciones/servicios y venta bienes). */
export const MONOTRIBUTO_CATEGORY_ANNUAL_LIMIT_ARS: Record<string, number> = {
  A: 10_277_988.13,
  B: 15_058_447.71,
  C: 21_113_696.52,
  D: 26_212_853.42,
  E: 30_833_964.37,
  F: 38_642_048.36,
  G: 46_211_109.37,
  H: 70_113_407.33,
  I: 78_479_211.62,
  J: 89_872_640.3,
  K: 108_357_084.05,
};

/** Fecha desde la cual rigen los topes embebidos. Actualizar junto con la tabla anterior. */
export const MONOTRIBUTO_LIMITS_REFERENCE_EFFECTIVE_FROM = '2026-02-01';

/** Cuadro oficial de categorías y topes (AFIP / ARCA). */
export const MONOTRIBUTO_OFFICIAL_CATEGORIES_URL = 'https://www.afip.gob.ar/monotributo/categorias.asp';

export function spanishTaxRegimeLabel(regime: TaxRegime | null | undefined): string {
  if (regime === 'responsable_inscripto') return 'Responsable inscripto';
  if (regime === 'monotributo') return 'Monotributo';
  return 'Sin configurar';
}

/** Compact label for narrow metric cells (2-col mobile). */
export function spanishTaxRegimeLabelCompact(regime: TaxRegime | null | undefined): string {
  if (regime === 'responsable_inscripto') return 'Resp. inscripto';
  return spanishTaxRegimeLabel(regime);
}

export function spanishArcaEnvironmentLabel(env: ArcaEnvironment | null | undefined): string {
  if (env === 'produccion') return 'Producción';
  if (env === 'homologacion') return 'Modo prueba';
  return '—';
}

/** Traduce fallas de WSAA/WSFE que el dueño puede resolver en Clave Fiscal. */
export function spanishArcaConnectError(raw: string | null | undefined): string {
  const message = (raw ?? '').trim();
  if (!message) return 'No se pudo conectar con ARCA.';
  if (message.toLowerCase().includes('computador no autorizado')) {
    return 'ARCA no autorizó este certificado. En Clave Fiscal, Administrador de Relaciones, delegá Facturación electrónica.';
  }
  return message;
}

/** Aviso post-conexión: facturar anduvo, padrón no. */
export const ARCA_PADRON_DELEGATION_HINT =
  'Ya podés facturar. Para buscar el nombre por CUIT, en Clave Fiscal delegá también Constancia de Inscripción.';

export function isPadronUnauthorizedError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'code' in err && (err as { code?: string }).code === 'padron_unauthorized') {
    return true;
  }
  const message = err instanceof Error ? err.message : String(err ?? '');
  const lower = message.toLowerCase();
  return lower.includes('constancia de inscripción') || lower.includes('padron_unauthorized');
}

/** Post-conexión: WSFE anduvo, padrón no. No uses el copy de “Buscar en ARCA”. */
export function padronWarningFromError(err: unknown): string | null {
  return isPadronUnauthorizedError(err) ? ARCA_PADRON_DELEGATION_HINT : null;
}

/** Display CUIT as `XX-XXXXXXXX-X` when 11 digits; otherwise digits as-is. */
export function formatCuitDisplay(cuit: string | null | undefined): string {
  const digits = normalizeCuitDigits(cuit ?? '');
  if (digits.length !== 11) return digits || '—';
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

export function spanishIvaRateLabel(rate: IvaRateOption | string | null | undefined): string {
  if (rate === '10.5') return '10,5 %';
  if (rate === '27') return '27 %';
  if (rate === '0') return '0 %';
  if (rate === 'exento') return 'Exento';
  if (rate === 'no_gravado') return 'No gravado';
  return '21 %';
}

export const IVA_RATE_OPTIONS: Array<{ id: IvaRateOption; label: string }> = [
  { id: '21', label: '21 %' },
  { id: '10.5', label: '10,5 %' },
  { id: '27', label: '27 %' },
  { id: '0', label: '0 %' },
  { id: 'exento', label: 'Exento' },
  { id: 'no_gravado', label: 'No gravado' },
];

export function normalizeCuitDigits(cuit: string): string {
  return cuit.replace(/\D/g, '').slice(0, 11);
}

export function isValidCuitDigits(cuit: string): boolean {
  return normalizeCuitDigits(cuit).length === 11;
}

export function monotributoLimitForCategory(category: string | null | undefined): number | null {
  if (!category) return null;
  const key = category.trim().toUpperCase().slice(0, 1);
  return MONOTRIBUTO_CATEGORY_ANNUAL_LIMIT_ARS[key] ?? null;
}

/** Promedio mensual de referencia (tope anual ÷ 12). */
export function monotributoMonthlyLimitForCategory(category: string | null | undefined): number | null {
  const annual = monotributoLimitForCategory(category);
  if (annual == null) return null;
  return Math.round((annual / 12) * 100) / 100;
}

export function formatMonotributoLimitsReferenceDate(isoDate = MONOTRIBUTO_LIMITS_REFERENCE_EFFECTIVE_FROM): string {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Short annual cap for enriched select labels, e.g. `$10,3M`. */
export function formatMonotributoShortCap(annualArs: number): string {
  return formatMoneyCompact(annualArs);
}

/** Enriched category option label: `A · hasta $10,3M`. */
export function monotributoCategorySelectLabel(category: string | null | undefined): string {
  if (!category) return '—';
  const key = category.trim().toUpperCase().slice(0, 1);
  const annual = MONOTRIBUTO_CATEGORY_ANNUAL_LIMIT_ARS[key];
  if (annual == null) return key;
  return `${key} · hasta ${formatMonotributoShortCap(annual)}`;
}

export const MONOTRIBUTO_CATEGORY_LETTERS = 'ABCDEFGHIJK'.split('');

export function monotributoCategoryPickerOptions(): Array<{ value: string; label: string }> {
  return MONOTRIBUTO_CATEGORY_LETTERS.map((letter) => ({
    value: letter,
    label: monotributoCategorySelectLabel(letter),
  }));
}

export type IibbSelection = 'convenio' | 'exento' | 'numero';

export const IIBB_CONVENIO_LABEL = 'Convenio Multilateral';
export const IIBB_EXENTO_LABEL = 'Exento';
export const IIBB_NUMERO_LABEL = 'Número de IIBB';

export const IIBB_PICKER_OPTIONS: Array<{ value: IibbSelection; label: string }> = [
  { value: 'convenio', label: IIBB_CONVENIO_LABEL },
  { value: 'exento', label: IIBB_EXENTO_LABEL },
  { value: 'numero', label: IIBB_NUMERO_LABEL },
];

/**
 * Clasifica el valor persistido de IIBB en la opción del selector.
 * Vacío → 'convenio' (el caso más común: adhesión monotributo).
 * 'Exento' / 'Convenio Multilateral' exactos → opción propia.
 * Cualquier otro valor → 'numero' con el valor como número libre.
 */
export function iibbToSelection(iibb: string | null | undefined): { selection: IibbSelection; numero: string } {
  const v = (iibb ?? '').trim();
  if (v === IIBB_EXENTO_LABEL) return { selection: 'exento', numero: '' };
  if (v === IIBB_CONVENIO_LABEL || !v) return { selection: 'convenio', numero: '' };
  return { selection: 'numero', numero: v };
}

/** Valor persistible de IIBB según la selección del selector. */
export function iibbSelectionToValue(selection: IibbSelection, numero: string): string {
  if (selection === 'numero') return numero.trim();
  return selection === 'exento' ? IIBB_EXENTO_LABEL : IIBB_CONVENIO_LABEL;
}

/** Etiqueta del trigger del selector (compacta; el número va en el input). */
export function iibbSelectionLabel(selection: IibbSelection): string {
  return IIBB_PICKER_OPTIONS.find((o) => o.value === selection)?.label ?? IIBB_NUMERO_LABEL;
}
